import AlertsColumns from "./alertsColumns";
import React, { useEffect, useMemo, useState } from "react";
import { fetchAlertsData, selector as alertsApi } from "./listAlertsSlice";
import {
  endAlertModal,
  selector as endAlertApi,
} from "../../components/endAlertModal/endAlertModalSlice";
import {
  sendDasAlert,
  selector as sendDasAlertSelector,
} from "./dasAlertSlice";
import {
  alertAction,
  selector as alertActionSelector,
} from "./alertActionSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  DataAvailabilityMessages,
  DefaultTopLevelGroup,
  MIMO_GROUP_ID,
  Status,
} from "../../common/constants";
import { useSpinner } from "../../customHooks";
import {
  IAlerts,
  IAlertsApi,
  normalizeGroupedAlerts,
  getGroupsListFromState,
} from "./utils";
import { selector as groupApi } from "../deviceManagement/groupSlice";
import {
  listApprovalAlert,
  selector as listApprovalAlertApi,
} from "../alerts/alertsNeedingApproval/listApprovalSlice";
import { actions as modalActions } from "../../components/modals/createAlert/createEditAlertModalSlice";
import { setEditAlertData } from "../../components/modals/createAlert/editAlertSlice";
import useNotification from "../../components/notification/useNotification";
import { actions as spinnerActions } from "../../components/ui/spinner/spinnerSlice";
import {
  IAlertActionPayload,
  IAlertData,
} from "../../common/types/alertsTypes";
import { isSuccessResponse } from "../../common/utils";
import { IInitialSliceState } from "../../common/types/commonTypes";
import { deleteAlertModal, selector as deleteAlertApi } from "../../components/deleteAlertModal/deleteAlertModalSlice";
import { usePermissions } from "../../customHooks/usePermissions";
import api from "../../api";
import { getAlertDetailsUrl } from "../../config";
import { useSearchParams } from "react-router-dom";


const EMPTY_ARRAY: any[] = [];

const useAlerts = () => {
  const dispatch: any = useDispatch();
  const { hasPermissions } = usePermissions();
  const canManageAlerts = hasPermissions("user.delete");

  const isMimoAccount: boolean = DefaultTopLevelGroup.ID == MIMO_GROUP_ID;

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const showModal = (): void => {
    dispatch(modalActions.showModal());
  };

  const [searchParams] = useSearchParams();

  const getInitialColumnFilters = (): any[] => {
    const initialFilters: any[] = [];

    const severity = searchParams.get("severity");
    const status = searchParams.get("status");

    if (status) {
      initialFilters.push({
        id: "status",
        value: status,
      });
    }

    if (severity) {
      initialFilters.push({
        id: "severity",
        value: severity,
      });
    }

    return initialFilters;
  };

  const onEditAlert = React.useCallback(
    (alertData: IAlertData): void => {
      dispatch(modalActions.turnOnEditMode());
      dispatch(modalActions.showModal());
      dispatch(setEditAlertData({ alertData }));
    },
    [dispatch],
  );

  const normalizeSearchText = (value: string): string => {
    return value.replace(/\s+/g, " ").trimStart();
  };

  const handleAlertActions = (command: string) => {
    const payload: IAlertActionPayload = { command };

    dispatch(alertAction(payload)).then((response) => {
      if (isSuccessResponse(response.payload)) {
        showSuccessNotification(response.payload.message);
      } else {
        showWarningNotification(response.payload.response.data.message);
      }
    });
  };

  const alertsData: IAlerts = useSelector(alertsApi);
  const createAlertStatus = useSelector(
  (state: any) => state.createAlert.status
);
  const endAlertResponse: IInitialSliceState = useSelector(endAlertApi);
  const sendDasAlertResponse: IInitialSliceState =
    useSelector(sendDasAlertSelector);
  const alertActionResponse: IInitialSliceState =
    useSelector(alertActionSelector);

  const newlyCreatedAlertId: string =
    useSelector(alertsApi)?.newlyCreatedAlertId;

  const groupApiResponse: any = useSelector(groupApi);
  const groupApiStatus: string = groupApiResponse.status;
  const groupsList = useMemo(
    () => getGroupsListFromState(groupApiResponse),
    [groupApiResponse],
  );
  const listApprovalApiStatus: string =
    useSelector(listApprovalAlertApi).status;

  const [alertsTableData, setAlertsTableData] = useState<IAlertsApi[]>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [columnFilters, setColumnFilters] = useState<any[]>(getInitialColumnFilters);

  const [loadedSearchAlertDetailsMap, setLoadedSearchAlertDetailsMap] =
    useState<Record<string, IAlertsApi>>({});

  const loadedSearchAlertDetailsRef = React.useRef<Record<string, IAlertsApi>>(
    {},
  );
  const loadingAlertDetailsRef = React.useRef<Record<string, boolean>>({});

  const [endAlertDialogState, setEndAlertDialogState] = useState<{
  show: boolean;
  alertId?: string;
  alertHistoryId?: string;
  alertMessage: string;
  actionLabel: string;
  affectedGroupNames: string[];
}>({
  show: false,
  alertMessage: "",
  actionLabel: "End",
  affectedGroupNames: [],
});

  const [deleteAlertDialogState, setDeleteAlertDialogState] = useState<{
  show: boolean;
  alertId: string;
  jobId: string;
  alertMessage: string;
  groupNames: string[];
}>({
  show: false,
  alertId: "",
  jobId: "",
  alertMessage: "",
  groupNames: [],
});

const [failedDevicesDialogState, setFailedDevicesDialogState] = useState<{
  show: boolean;
  deviceIds: string[];
}>({
  show: false,
  deviceIds: [],
});

  const [openAlertActionDropdownId, setOpenAlertActionDropdownId] =
  useState<string | null>(null);

  const [resetExpandedRowsKey, setResetExpandedRowsKey] = useState<number>(0);

  const isGlobalSearchActive = Boolean(globalFilter?.trim());

  const latestAlertDetailsRefs = React.useRef({
  groupsList,
  showWarningNotification,
});

React.useEffect(() => {
  latestAlertDetailsRefs.current = {
    groupsList,
    showWarningNotification,
  };
}, [groupsList, showWarningNotification]);

  const listApprovalAlertResponse = useSelector(listApprovalAlertApi);
  const approvalAlertsCount = listApprovalAlertResponse?.data?.length || 0;
  const alertGroupOptions = useSelector(alertsApi)?.alertGroupOptions || EMPTY_ARRAY;
  const deleteAlertApiResponse = useSelector(deleteAlertApi);

  useSpinner(sendDasAlertResponse.status);
  useSpinner(alertActionResponse.status);
  useSpinner(endAlertResponse.status);
  useSpinner();

  useEffect(() => {
    setLoadedSearchAlertDetailsMap({});
    loadedSearchAlertDetailsRef.current = {};
    loadingAlertDetailsRef.current = {};
  }, [globalFilter]);

  useEffect(() => {
    dispatch(listApprovalAlert());
  }, []);

  useEffect(() => {
    dispatch(spinnerActions.setApiStatus(alertsData.status));
  }, [alertsData.status]);

  useEffect(() => {
    if (alertsData.alerts) {
      const normalizedAlerts = normalizeGroupedAlerts(
        alertsData.alerts,
        groupsList,
        isGlobalSearchActive,
      );

      const mergedAlerts = isGlobalSearchActive
        ? normalizedAlerts.map((alert) => {
            return loadedSearchAlertDetailsMap[alert.alertId] || alert;
          })
        : normalizedAlerts;

      setAlertsTableData(mergedAlerts);
      setTotalCount(alertsData.totalCount || 0);
    }
  }, [
    alertsData.alerts,
    alertsData.totalCount,
    groupsList,
    isGlobalSearchActive,
    loadedSearchAlertDetailsMap,
  ]);

  useEffect(() => {
    const normalizedGlobalFilter = globalFilter.trim();

    dispatch(
      fetchAlertsData({
        pageIndex,
        pageSize,
        globalFilter: normalizedGlobalFilter,
        columnFilters,
      }),
    );
  }, [pageIndex, pageSize, globalFilter, JSON.stringify(columnFilters)]);

  useEffect(() => {
  if (createAlertStatus === Status.SUCCESS) {
    setPageIndex(0);
    setPageSize(10);
    setGlobalFilter("");
    setColumnFilters([]);
  }
}, [createAlertStatus]);

  useEffect(() => {
    if (endAlertResponse.status === Status.SUCCESS) {
      setPageIndex(0);
      setPageSize(10);
      setGlobalFilter("");
      setColumnFilters([]);
    }
  }, [endAlertResponse.status]);

  useEffect(() => {
    if (deleteAlertApiResponse.status === Status.SUCCESS) {
      setPageIndex(0);
      setPageSize(10);
      setGlobalFilter("");
      setColumnFilters([]);
    }
  }, [deleteAlertApiResponse.status]);

  const handleLoadSearchAlertDetails = React.useCallback(
    async (alertId: string, currentAlertRow?: IAlertsApi): Promise<boolean> => {
      if (!isGlobalSearchActive) {
        return true;
      }

      if (loadedSearchAlertDetailsRef.current[alertId]) {
        return true;
      }

      if (loadingAlertDetailsRef.current[alertId]) {
        return false;
      }

      loadingAlertDetailsRef.current[alertId] = true;
      dispatch(spinnerActions.setApiStatus(Status.PENDING));


      try {
        const response = await api.get(
          `${getAlertDetailsUrl}?alertId=${alertId}`,
        );
        const alertDetailsResponse = response.data;

        const normalizedAlert = normalizeGroupedAlerts(
  [
    {
      ...currentAlertRow,
      alertId: alertDetailsResponse.alertId || alertId,
      alertDetails: alertDetailsResponse.alertDetails,
      groups: alertDetailsResponse.groups || [],
      creationTime:
        alertDetailsResponse.creationTime ||
        currentAlertRow?.creationTime,
    } as IAlertsApi,
  ],
  latestAlertDetailsRefs.current.groupsList,
  false,
)[0];

        const loadedAlertDetails = {
          ...normalizedAlert,
          isSearchResultRow: false,
          isSearchDetailsLoaded: true,
        };

        loadedSearchAlertDetailsRef.current = {
          ...loadedSearchAlertDetailsRef.current,
          [alertId]: loadedAlertDetails,
        };

        setLoadedSearchAlertDetailsMap((previousState) => ({
          ...previousState,
          [alertId]: loadedAlertDetails,
        }));

        dispatch(spinnerActions.setApiStatus(Status.SUCCESS));
        return true;
      } catch (error) {
        dispatch(spinnerActions.setApiStatus(Status.FAILED));
        latestAlertDetailsRefs.current.showWarningNotification(
  "Unable to load alert details right now.",
);
        return false;
      } finally {
        loadingAlertDetailsRef.current[alertId] = false;
      }
    },
    [dispatch, isGlobalSearchActive],
  );

  const handleOpenEndAlertDialog = React.useCallback(
    ({
      alertId,
      alertHistoryId,
      alertMessage,
      actionLabel,
      affectedGroupNames = [],
    }:  {
    alertId?: string;
    alertHistoryId?: string;
    alertMessage: string;
    actionLabel: string;
    affectedGroupNames?: string[];
  }) => {
    setEndAlertDialogState({
      show: true,
      alertId,
      alertHistoryId,
      alertMessage,
      actionLabel,
      affectedGroupNames,
    });
    },
    [],
  );

  const handleCloseEndAlertDialog = React.useCallback(() => {
    setEndAlertDialogState({
    show: false,
    alertMessage: "",
    actionLabel: "End",
    affectedGroupNames: [],
  });
  }, []);

  const handleConfirmEndAlert = React.useCallback(() => {
    dispatch(
      endAlertModal({
        alertId: endAlertDialogState.alertId,
        alertHistoryId: endAlertDialogState.alertHistoryId,
      }),
    ).then((response) => {
      if (isSuccessResponse(response.payload)) {
        dispatch(fetchAlertsData({ pageIndex: 0, pageSize: 10 }));
        showSuccessNotification(response.payload.message);
      } else {
        const errorMessage =
          response.payload?.response?.data?.message ||
          response.payload?.response?.data?.error?.message ||
          "Failed to end alert";

        showWarningNotification(errorMessage);
      }
    });

    handleCloseEndAlertDialog();
  }, [
    dispatch,
    endAlertDialogState.alertId,
    endAlertDialogState.alertHistoryId,
    handleCloseEndAlertDialog,
    showSuccessNotification,
    showWarningNotification,
  ]);

  const handleOpenDeleteAlertDialog = React.useCallback(
  ({
    alertId,
    jobId,
    alertMessage,
    groupNames,
  }: {
    alertId: string;
    jobId: string;
    alertMessage: string;
    groupNames: string[];
  }) => {
    setDeleteAlertDialogState({
      show: true,
      alertId,
      jobId,
      alertMessage,
      groupNames,
    });
  },
  []
);

const handleCloseDeleteAlertDialog = React.useCallback(() => {
  setDeleteAlertDialogState((previousState) => ({
    ...previousState,
    show: false,
  }));
}, []);

const handleConfirmDeleteAlert = React.useCallback(() => {
  dispatch(
    deleteAlertModal({
      alertId: deleteAlertDialogState.alertId,
      jobId: deleteAlertDialogState.jobId,
    })
  ).then((response) => {
    if (isSuccessResponse(response.payload)) {
      dispatch(fetchAlertsData({ pageIndex: 0, pageSize: 10 }));
      showSuccessNotification(response.payload.message);
    } else {
      const errorMessage =
        response.payload?.response?.data?.message ||
        response.payload?.response?.data?.error?.message ||
        "Failed to delete alert";

      showWarningNotification(errorMessage);
    }
  });

  handleCloseDeleteAlertDialog();
}, [
  deleteAlertDialogState.alertId,
  deleteAlertDialogState.jobId,
  dispatch,
  handleCloseDeleteAlertDialog,
  showSuccessNotification,
  showWarningNotification,
]);

const handleOpenFailedDevicesDialog = React.useCallback(
  (deviceIds: string[]) => {
    setFailedDevicesDialogState({
      show: true,
      deviceIds,
    });
  },
  []
);

const handleCloseFailedDevicesDialog = React.useCallback(() => {
  setFailedDevicesDialogState({
    show: false,
    deviceIds: [],
  });
}, []);

  const handleToggleAlertActionDropdown = React.useCallback(
  (dropdownId: string) => {
    setOpenAlertActionDropdownId((currentDropdownId) =>
      currentDropdownId === dropdownId ? null : dropdownId
    );
  },
  []
);

const handleCloseAlertActionDropdown = React.useCallback(() => {
  setOpenAlertActionDropdownId(null);
}, []);

React.useEffect(() => {
  if (!openAlertActionDropdownId) {
    return;
  }

  const handleDocumentClick = () => {
    setOpenAlertActionDropdownId(null);
  };

  document.addEventListener("click", handleDocumentClick);

  return () => {
    document.removeEventListener("click", handleDocumentClick);
  };
}, [openAlertActionDropdownId]);

  const alertsColumns = useMemo(
  () =>
    AlertsColumns({
      alertGroupOptions,
      canManageAlerts,
      onEditAlert,
      isGlobalSearchActive,
      onLoadSearchAlertDetails: handleLoadSearchAlertDetails,
      onOpenEndAlertDialog: handleOpenEndAlertDialog,
      onOpenDeleteAlertDialog: handleOpenDeleteAlertDialog,
      onOpenFailedDevicesDialog: handleOpenFailedDevicesDialog,
      openAlertActionDropdownId,
      onToggleAlertActionDropdown: handleToggleAlertActionDropdown,
      onCloseAlertActionDropdown: handleCloseAlertActionDropdown,
    }),
  [
    alertGroupOptions,
    canManageAlerts,
    onEditAlert,
    isGlobalSearchActive,
    handleLoadSearchAlertDetails,
    handleOpenEndAlertDialog,
    handleOpenDeleteAlertDialog,
    handleOpenFailedDevicesDialog,
    openAlertActionDropdownId,
    handleToggleAlertActionDropdown,
    handleCloseAlertActionDropdown,
  ],
);

  const isApiLoading: boolean =
    alertsData.status === Status.PENDING ||
    groupApiStatus === Status.PENDING ||
    listApprovalApiStatus === Status.PENDING;

  const noDataMessage: string = isApiLoading
    ? DataAvailabilityMessages.ALERT_DATA_LOADING
    : DataAvailabilityMessages.NO_ALERT_AVAILABLE;

  const refreshAlerts = () => {
     setResetExpandedRowsKey((previousKey) => previousKey + 1);

    dispatch(
      fetchAlertsData({ pageIndex, pageSize, globalFilter, columnFilters }),
    );
  };

  const handleGlobalFilterChange = React.useCallback((updater) => {
    setGlobalFilter((previousGlobalFilter) => {
      const nextValue =
        typeof updater === "function" ? updater(previousGlobalFilter) : updater;

      const normalizedSearchText = normalizeSearchText(String(nextValue || ""));

      if (previousGlobalFilter !== normalizedSearchText) {
        setResetExpandedRowsKey((previousKey) => previousKey + 1);

        setPageIndex((previousPageIndex) =>
          previousPageIndex === 0 ? previousPageIndex : 0,
        );
      }

      return normalizedSearchText;
    });
  }, []);

  const handleColumnFiltersChange = React.useCallback((updater) => {
    setColumnFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;

      if (JSON.stringify(prev) !== JSON.stringify(next)) {
        setResetExpandedRowsKey((previousKey) => previousKey + 1);
        setPageIndex((p) => (p === 0 ? p : 0));
      }

      return next;
    });
  }, []);

  return {
    alertsTableData,
    alertsColumns,
    globalFilter,
    setGlobalFilter,
    refreshAlerts,
    noDataMessage,
    newlyCreatedAlertId,
    handleAlertActions,
    showModal,
    isMimoAccount,
    totalCount,
    setTotalCount,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    columnFilters,
    setColumnFilters,
    approvalAlertsCount,
    listApprovalApiStatus,
    handleGlobalFilterChange,
    handleColumnFiltersChange,
    endAlertDialogState,
    handleCloseEndAlertDialog,
    handleConfirmEndAlert,
    deleteAlertDialogState,
    handleCloseDeleteAlertDialog,
    handleConfirmDeleteAlert,
    failedDevicesDialogState,
    handleCloseFailedDevicesDialog,
    resetExpandedRowsKey,
  };
};

export default useAlerts;
